import { z } from 'zod';
import { Capability, GlobalRole, Locale, PrincipalKind, TeamRole, UserStatus, Visibility } from './enums.js';
import { IsoDateTime, ShortText, Title, Uuid, Version, boundedList } from './common.js';

export const TeamSummary = z.object({ id: Uuid, name: z.string(), slug: z.string() });
export type TeamSummary = z.infer<typeof TeamSummary>;

export const ExternalIdentityView = z.object({
  id: Uuid,
  provider: z.string(),
  externalAccountId: z.string(),
  displayHandle: z.string().nullable(),
  verificationMethod: z.string(),
  verifiedAt: IsoDateTime.nullable(),
});

export const MePermissions = z.object({
  isAdmin: z.boolean(),
  isDeveloper: z.boolean(),
  isProduct: z.boolean(),
  canTriage: z.boolean(),
  canCreateProject: z.boolean(),
  canCreateTeam: z.boolean(),
  canCreateDevelopmentWork: z.boolean(),
  canManageLabels: z.boolean(),
  canRecordEngineeringProgress: z.boolean(),
  reportTiers: z.array(z.enum(['GENERAL', 'TECHNICAL', 'SENSITIVE'])),
  managedTeamIds: z.array(Uuid),
});
export type MePermissions = z.infer<typeof MePermissions>;

export const MeView = z.object({
  id: Uuid,
  displayName: z.string(),
  email: z.string().nullable(),
  locale: Locale,
  timezone: z.string(),
  status: UserStatus,
  roles: z.array(GlobalRole),
  capabilities: z.array(Capability),
  teams: z.array(TeamSummary.extend({ role: TeamRole })),
  groups: z.array(z.object({ id: Uuid, name: z.string() })),
  identities: z.array(ExternalIdentityView),
  permissions: MePermissions,
  authorizationRevision: z.number().int(),
});
export type MeView = z.infer<typeof MeView>;

export const UpdateProfile = z
  .object({
    displayName: Title.optional(),
    locale: Locale.optional(),
    timezone: z.string().min(1).max(64).optional(),
  })
  .strict();
export type UpdateProfile = z.infer<typeof UpdateProfile>;

/** A person adds one of their own GitHub accounts by username (ADR-0011). */
export const LinkMyGitHubAccount = z
  .object({ login: z.string().trim().min(1).max(40) })
  .strict();
export type LinkMyGitHubAccount = z.infer<typeof LinkMyGitHubAccount>;

export const UserListItem = z.object({
  id: Uuid,
  displayName: z.string(),
  email: z.string().nullable(),
  status: UserStatus,
  roles: z.array(GlobalRole),
  capabilities: z.array(Capability),
  lastLoginAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type UserListItem = z.infer<typeof UserListItem>;

export const SetUserAccess = z
  .object({
    roles: z.array(GlobalRole).max(3),
    capabilities: z.array(Capability).max(10),
    reason: z.string().min(1).max(500),
  })
  .strict();
export type SetUserAccess = z.infer<typeof SetUserAccess>;

export const SetUserStatus = z
  .object({
    status: z.enum(['ACTIVE', 'SUSPENDED']),
    reason: z.string().min(1).max(500),
    revokeSessions: z.boolean().optional(),
  })
  .strict();
export type SetUserStatus = z.infer<typeof SetUserStatus>;

export const TeamView = z.object({
  id: Uuid,
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  archivedAt: IsoDateTime.nullable(),
  version: Version,
  memberCount: z.number().int(),
  members: z.array(z.object({ user: z.object({ id: Uuid, displayName: z.string(), email: z.string().nullable() }), role: TeamRole })),
  availableActions: z.array(z.string()),
});
export type TeamView = z.infer<typeof TeamView>;

export const CreateTeam = z
  .object({
    name: Title,
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]{1,40}$/),
    description: ShortText.optional(),
    managerIds: boundedList(Uuid).optional(),
  })
  .strict();
export type CreateTeam = z.infer<typeof CreateTeam>;

export const UpdateTeam = z
  .object({ expectedVersion: Version, name: Title.optional(), description: ShortText.nullable().optional() })
  .strict();
export type UpdateTeam = z.infer<typeof UpdateTeam>;

export const SetTeamMember = z.object({ userId: Uuid, role: TeamRole }).strict();
export type SetTeamMember = z.infer<typeof SetTeamMember>;

export const GroupView = z.object({
  id: Uuid,
  name: z.string(),
  description: z.string().nullable(),
  memberCount: z.number().int(),
  members: z.array(z.object({ id: Uuid, displayName: z.string(), email: z.string().nullable() })),
});
export type GroupView = z.infer<typeof GroupView>;

export const CreateGroup = z.object({ name: Title, description: ShortText.optional() }).strict();
export type CreateGroup = z.infer<typeof CreateGroup>;
export const SetGroupMembers = z.object({ add: boundedList(Uuid).optional(), remove: boundedList(Uuid).optional() }).strict();
export type SetGroupMembers = z.infer<typeof SetGroupMembers>;

export const AccessGrantInput = z.object({ principalKind: PrincipalKind, principalId: Uuid }).strict();
export type AccessGrantInput = z.infer<typeof AccessGrantInput>;

export const AccessGrantView = z.object({
  principalKind: PrincipalKind,
  principalId: Uuid,
  principalName: z.string(),
});
export type AccessGrantView = z.infer<typeof AccessGrantView>;

export const SetVisibility = z
  .object({
    expectedVersion: Version,
    visibility: Visibility,
    grants: boundedList(AccessGrantInput),
    reason: z.string().max(500).optional(),
  })
  .strict();
export type SetVisibility = z.infer<typeof SetVisibility>;

export const PrincipalSearchResult = z.object({
  kind: PrincipalKind,
  id: Uuid,
  name: z.string(),
  detail: z.string().nullable(),
});
export type PrincipalSearchResult = z.infer<typeof PrincipalSearchResult>;

export const AuditEntryView = z.object({
  id: Uuid,
  occurredAt: IsoDateTime,
  eventType: z.string(),
  actorKind: z.string(),
  actor: z.object({ id: Uuid, displayName: z.string() }).nullable(),
  aggregateKind: z.string(),
  aggregateId: Uuid,
  source: z.string(),
  summary: z.record(z.string(), z.unknown()),
});
export type AuditEntryView = z.infer<typeof AuditEntryView>;
